import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faBars, faCrown } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import axios from 'axios';
import { useBaseUrl } from '../../utils/utils';
import { logoutModernAuth } from '../../utils/sessionAuth';
import { DASHBOARD_REGISTRY, DashboardId } from '../../config/dashboardRegistry';

/**
 * Shared shell for all 7 internal dashboards (tracker Phase 10) - one
 * shell, not seven, same reasoning ProviderShell.tsx gave for the customer
 * side: a per-dashboard sidebar component rather than forcing every
 * internal dashboard into the institution-side dashboard_menu.tsx. Each
 * dashboard passes its own sub-nav (e.g. Super Admin's 8 pages); the shell
 * itself only owns the outer chrome, the InternalUser identity, and
 * cross-dashboard navigation shared by all seven.
 */

export const getStoredInternalToken = () =>
    localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';

export const useInternalApi = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const token = getStoredInternalToken();

    useEffect(() => {
        if (!token) navigate('/internal/login');
    }, [token, navigate]);

    const api = axios.create({
        baseURL: baseUrl,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return { api, baseUrl, token };
};

type SubNavItem = { to: string; label: string; icon: IconDefinition; end?: boolean };

const InternalShell: React.FC<{ activeDashboard: DashboardId; subNav: SubNavItem[]; children: React.ReactNode }> = ({ activeDashboard, subNav, children }) => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState('');
    const [grantedInternal, setGrantedInternal] = useState<DashboardId[]>([]);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const token = getStoredInternalToken();
        if (!token) return;
        axios.get(`${baseUrl}platform/my-dashboards/`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                const data = res.data?.data;
                setDisplayName(data?.display_name || '');
                setGrantedInternal((data?.dashboards || []).filter((d: any) => d.is_internal).map((d: any) => d.id));
            })
            .catch(() => {});
    }, [baseUrl]);

    const handleLogout = async () => {
        await logoutModernAuth(baseUrl);
        navigate('/internal/login');
    };

    const meta = DASHBOARD_REGISTRY[activeDashboard];

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafb' }}>
            <aside
                style={{
                    width: 250, flexShrink: 0, background: '#405189', color: '#fff',
                    position: 'fixed', top: 0, bottom: 0, left: mobileOpen ? 0 : undefined,
                    display: 'flex', flexDirection: 'column', zIndex: 1000,
                }}
                className={`internal-sidebar ${mobileOpen ? 'open' : ''}`}
            >
                <div style={{ padding: '20px 18px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FontAwesomeIcon icon={meta?.icon || faCrown} size="lg" />
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{meta?.label || 'Uzima Internal'}</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.75 }}>Internal Dashboard</div>
                        </div>
                    </div>
                </div>

                <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
                    {subNav.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setMobileOpen(false)}
                            style={({ isActive }) => ({
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 12px', borderRadius: 10, marginBottom: 3,
                                color: '#fff', textDecoration: 'none', fontSize: '0.84rem',
                                background: isActive ? 'rgba(255,255,255,0.16)' : 'transparent',
                                fontWeight: isActive ? 600 : 400,
                            })}
                        >
                            <FontAwesomeIcon icon={item.icon} style={{ width: 16 }} />
                            {item.label}
                        </NavLink>
                    ))}

                    {grantedInternal.length > 1 && (
                        <>
                            <div style={{ margin: '14px 12px 6px', fontSize: '0.68rem', textTransform: 'uppercase', opacity: 0.6, fontWeight: 700 }}>
                                Other Internal Dashboards
                            </div>
                            {grantedInternal.filter(id => id !== activeDashboard).map(id => {
                                const otherMeta = DASHBOARD_REGISTRY[id];
                                if (!otherMeta) return null;
                                return (
                                    <a
                                        key={id}
                                        href={otherMeta.basePath}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, marginBottom: 3, color: '#fff', opacity: 0.75, textDecoration: 'none', fontSize: '0.8rem' }}
                                    >
                                        <FontAwesomeIcon icon={otherMeta.icon} style={{ width: 16 }} />
                                        {otherMeta.label}
                                    </a>
                                );
                            })}
                        </>
                    )}
                </nav>

                <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                    <a href="/dashboards" style={{ display: 'block', color: '#fff', fontSize: '0.78rem', opacity: 0.85, marginBottom: 10, textDecoration: 'none' }}>
                        Switch dashboard
                    </a>
                    <button
                        onClick={handleLogout}
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: '0.78rem', width: '100%', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} /> Sign out
                    </button>
                </div>
            </aside>

            <div className="internal-content" style={{ marginLeft: 250, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <header style={{
                    background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 900,
                }}>
                    <button
                        className="internal-mobile-toggle"
                        onClick={() => setMobileOpen(o => !o)}
                        style={{ display: 'none', background: 'transparent', border: 'none', fontSize: '1.1rem' }}
                    >
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{displayName}</div>
                        <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{meta?.description}</div>
                    </div>
                </header>

                <main style={{ flex: 1, padding: '20px 24px' }}>
                    {children}
                </main>
            </div>

            <style>{`
                @media (max-width: 900px) {
                    .internal-sidebar { transform: translateX(-100%); transition: transform 0.2s ease; }
                    .internal-sidebar.open { transform: translateX(0); }
                    .internal-content { margin-left: 0 !important; }
                    .internal-mobile-toggle { display: inline-block !important; }
                }
            `}</style>
        </div>
    );
};

export default InternalShell;
